/**
 * Guest-invite link helpers.
 *
 * A guest link carries the raw invite code in the URL FRAGMENT (hash) of a
 * public Orion route: `<origin>/meet/guest#<code>`. The code is a ~22-char
 * base64url string; we still encode/decode defensively so the round-trip is
 * exact regardless of the code alphabet the backend chooses.
 *
 * SECURITY: the code is a secret. It lives in the FRAGMENT — never the path or
 * query string — because fragments are stripped by the browser before a request
 * is sent: they never reach the server, CDN/ingress access logs, the `Referer`
 * header, or the navigation breadcrumb (which records `location.pathname` only).
 * These helpers never log the code.
 */

/**
 * The public route path the guest-join page is mounted at. The code is NOT a
 * path segment — it rides in the fragment (`#<code>`), so the route is static.
 */
export const GUEST_MEET_ROUTE = "/meet/guest";

/** Build the public guest-join link for a raw invite code. */
export function buildGuestInviteLink(
  code: string,
  origin: string = window.location.origin,
): string {
  return `${origin}${GUEST_MEET_ROUTE}#${encodeURIComponent(code)}`;
}

/**
 * Read the invite code from the current URL fragment.
 *
 * Returns `undefined` when the fragment is empty/absent or malformed (a
 * `decodeURIComponent` failure), so callers collapse both to the same generic
 * "invite no longer valid" screen.
 */
export function readGuestInviteCodeFromHash(
  hash: string = window.location.hash,
): string | undefined {
  const raw = hash.replace(/^#/, "");
  if (!raw) {
    return undefined;
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return undefined;
  }
}
